import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { HexDisplay, DigestDisplay } from '@/components/shared/HexDisplay';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Landmark } from 'lucide-react';
import type { GovernanceVoting, VoteStatus } from '@/types/governance';

interface VoteHeaderProps {
    vote: GovernanceVoting;
    voteStatus: VoteStatus;
}

export function VoteHeader({ vote, voteStatus }: VoteHeaderProps) {
    const getVoteStatus = () => {
        const currentTime = Date.now();

        if (voteStatus === 'not_started') {
            return {
                label: 'Not Started',
                color: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
            };
        }

        if (voteStatus === 'active') {
            return {
                label: 'Active',
                color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
            };
        }

        // Vote has ended
        if (vote.result === 'Passed') {
            return {
                label: 'Passed',
                color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
            };
        }

        if (vote.result === 'NotPassed') {
            return {
                label: 'Not Passed',
                color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
            };
        }

        return {
            label: 'Ended',
            color: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
        };
    };

    const status = getVoteStatus();

    return (
        <Card className='bg-[#C0E6FF]/10 backdrop-blur-md border border-white/20 rounded-lg shadow-lg'>
            <CardHeader>
                <div className="space-y-2">
                    <CardTitle className="flex items-center gap-2 text-2xl">
                        <Landmark className="h-6 w-6" />
                        Governance Vote
                    </CardTitle>
                    <div className="flex items-center gap-2">
                        <Badge className={status.color}>
                            {status.label}
                        </Badge>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                <div>
                    <h2 className="text-xl font-semibold mb-2">Vote ID</h2>
                    <div className="w-full">
                        <HexDisplay
                            value={vote.id}
                            className="text-sm text-muted-foreground w-full"
                            showCopyIcon={true}
                        />
                    </div>
                </div>

                <div>
                    <h2 className="text-xl font-semibold mb-2">Description</h2>
                    <div className="prose prose-slate dark:prose-invert max-w-4xl">
                        <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                        >
                            {vote.description}
                        </ReactMarkdown>
                    </div>
                </div>

                {vote.digests.length > 0 && (
                    <div>
                        <h2 className="text-xl font-semibold mb-2">Transaction Digests</h2>
                        <div className="space-y-1">
                            {vote.digests.map((digest, index) => (
                                <div key={index} className="w-full">
                                    <DigestDisplay
                                        digest={digest}
                                        className="text-sm text-muted-foreground w-full"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
} 